import { useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Building2, ChevronDown, ChevronRight, Shield, UserCog } from "lucide-react";
import { toast } from "sonner";

import { api, endpoints, type AdminGestore, type AdminUserRow } from "@/api/client";
import { useAuth } from "@/lib/AuthProvider";
import { enterAzienda } from "@/lib/azienda";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type AuditRow = {
  id: string;
  action: string;
  path: string | null;
  created_at: string;
  user_email: string | null;
};

const AZIENDA_STATUS: Record<string, string> = {
  active: "Attiva",
  deactivating: "In disattivazione",
  pending_payment: "In attesa di pagamento",
  removed: "Rimossa",
};

const SUB_STATUS: Record<string, string> = {
  active: "attivo",
  trialing: "attivo · periodo promozionale",
  past_due: "pagamento in sospeso",
};

function fmtDate(iso: string | null) {
  return iso ? new Date(iso).toLocaleDateString("it-IT") : "—";
}

function fmtEuro(cents: number) {
  return (cents / 100).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

export function AdminUsers() {
  const auth = useAuth();
  const [selected, setSelected] = useState<string | null>(null);
  const [hideTecnici, setHideTecnici] = useState(true);

  const usersQ = useQuery({
    queryKey: ["admin", "users"],
    queryFn: endpoints.users.list,
  });

  const gestoriQ = useQuery({
    queryKey: ["admin", "gestori"],
    queryFn: endpoints.users.gestori,
  });

  const auditQ = useQuery({
    queryKey: ["admin", "audit", selected],
    queryFn: () =>
      api.get<{ items: AuditRow[] }>(`/users/${encodeURIComponent(selected!)}/audit`),
    enabled: Boolean(selected),
  });

  const onImpersonate = async (email: string) => {
    try {
      await auth.startImpersonate(email);
      toast.success(`Impersonificazione attiva: ${email}`);
    } catch (e) {
      toast.error(String(e));
    }
  };

  /** Impersona il gestore e apre subito l'azienda indicata (header X-Azienda). */
  const onEnterAzienda = async (gestore: string, aziendaId: string, ragioneSociale: string) => {
    try {
      await auth.startImpersonate(gestore);
      toast.success(`Entro in ${ragioneSociale} come ${gestore}`);
      enterAzienda(aziendaId, "/");
    } catch (e) {
      toast.error(String(e));
    }
  };

  const users = (usersQ.data?.items ?? []).filter((u) => !(hideTecnici && u.is_azienda));
  const nTecnici = (usersQ.data?.items ?? []).filter((u) => u.is_azienda).length;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-2">
        <Shield className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-semibold">Amministrazione utenti</h1>
      </div>

      <section className="rounded-lg border bg-card" data-testid="admin-gestori">
        <h2 className="flex items-center gap-2 border-b px-4 py-3 font-medium">
          <Building2 className="h-4 w-4" />
          Gestori multi-azienda
          {gestoriQ.data && (
            <span className="text-xs font-normal text-muted-foreground">({gestoriQ.data.count})</span>
          )}
        </h2>
        {gestoriQ.isLoading && <p className="p-4 text-sm text-muted-foreground">Caricamento…</p>}
        {gestoriQ.isError && (
          <p className="p-4 text-sm text-destructive">Errore nel caricamento dei gestori.</p>
        )}
        {gestoriQ.data?.count === 0 && (
          <p className="p-4 text-sm text-muted-foreground">Nessun gestore multi-azienda registrato.</p>
        )}
        {gestoriQ.data?.items.map((g) => (
          <GestoreRow
            key={g.email}
            g={g}
            canImpersonate={g.email !== auth.realEmail}
            onImpersonate={() => void onImpersonate(g.email)}
            onEnterAzienda={(id, rs) => void onEnterAzienda(g.email, id, rs)}
          />
        ))}
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-lg border bg-card">
          <div className="flex items-center justify-between gap-2 border-b px-4 py-3">
            <h2 className="font-medium">Utenti registrati</h2>
            {nTecnici > 0 && (
              <label className="flex items-center gap-1 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={hideTecnici}
                  onChange={(e) => setHideTecnici(e.target.checked)}
                />
                Nascondi account tecnici ({nTecnici})
              </label>
            )}
          </div>
          <div className="max-h-[480px] overflow-y-auto">
            {usersQ.isLoading && (
              <p className="p-4 text-sm text-muted-foreground">Caricamento…</p>
            )}
            {users.map((u) => (
              <UserRowItem
                key={u.id}
                u={u}
                selected={selected === u.email}
                realEmail={auth.realEmail}
                onSelect={() => setSelected(u.email)}
                onImpersonate={() => void onImpersonate(u.email)}
                onEnterAzienda={() =>
                  u.managed_by &&
                  u.azienda_id &&
                  void onEnterAzienda(u.managed_by, u.azienda_id, u.ragione_sociale ?? u.email)
                }
              />
            ))}
          </div>
        </section>

        <section className="rounded-lg border bg-card">
          <h2 className="border-b px-4 py-3 font-medium">
            Audit {selected ? `— ${selected}` : ""}
          </h2>
          {!selected && (
            <p className="p-4 text-sm text-muted-foreground">
              Seleziona un utente per vedere le attività.
            </p>
          )}
          {selected && (
            <div className="max-h-[480px] overflow-y-auto">
              {auditQ.isLoading && (
                <p className="p-4 text-sm text-muted-foreground">Caricamento audit…</p>
              )}
              {auditQ.data?.items.map((a) => (
                <div key={a.id} className="border-b px-4 py-2 text-xs last:border-0">
                  <div className="font-medium">{a.action}</div>
                  <div className="text-muted-foreground">
                    {new Date(a.created_at).toLocaleString("it-IT")}
                    {a.path && ` · ${a.path}`}
                  </div>
                </div>
              ))}
              {auditQ.data?.items.length === 0 && (
                <p className="p-4 text-sm text-muted-foreground">Nessuna attività.</p>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function Badge({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide",
        className ?? "bg-muted text-muted-foreground",
      )}
    >
      {children}
    </span>
  );
}

function UserRowItem({
  u,
  selected,
  realEmail,
  onSelect,
  onImpersonate,
  onEnterAzienda,
}: {
  u: AdminUserRow;
  selected: boolean;
  realEmail: string;
  onSelect: () => void;
  onImpersonate: () => void;
  onEnterAzienda: () => void;
}) {
  const canImpersonate = !u.is_admin && u.email !== realEmail;
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-2 border-b px-4 py-3 text-sm last:border-0",
        selected && "bg-muted/50",
      )}
    >
      <button type="button" className="flex-1 text-left" onClick={onSelect}>
        <div className="flex items-center gap-2 font-medium">
          {u.is_azienda ? (u.ragione_sociale ?? u.email) : u.email}
          {u.is_gestore && <Badge className="bg-sky-100 text-sky-800">Gestore</Badge>}
          {u.is_azienda && <Badge>Account tecnico</Badge>}
        </div>
        <div className="text-xs text-muted-foreground">
          {u.is_azienda ? (
            <>
              Azienda di {u.managed_by ?? "gestore sconosciuto"}
              {u.azienda_status && ` · ${AZIENDA_STATUS[u.azienda_status] ?? u.azienda_status}`}
              {" · "}
              <span className="font-mono">{u.email}</span>
            </>
          ) : u.is_admin ? (
            "Admin"
          ) : (
            "Utente"
          )}
          {u.last_login_at && ` · ultimo accesso ${new Date(u.last_login_at).toLocaleString("it-IT")}`}
        </div>
      </button>
      {u.is_azienda && u.managed_by && u.azienda_id ? (
        <Button size="sm" variant="secondary" onClick={onEnterAzienda}>
          <Building2 className="mr-1 h-4 w-4" />
          Entra nell'azienda
        </Button>
      ) : (
        canImpersonate && (
          <Button size="sm" variant="secondary" onClick={onImpersonate}>
            <UserCog className="mr-1 h-4 w-4" />
            Accedi come
          </Button>
        )
      )}
    </div>
  );
}

function GestoreRow({
  g,
  canImpersonate,
  onImpersonate,
  onEnterAzienda,
}: {
  g: AdminGestore;
  canImpersonate: boolean;
  onImpersonate: () => void;
  onEnterAzienda: (aziendaId: string, ragioneSociale: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const sub = g.subscription_status ? (SUB_STATUS[g.subscription_status] ?? g.subscription_status) : "nessun abbonamento";
  return (
    <div className="border-b last:border-0">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm">
        <button
          type="button"
          className="flex flex-1 items-center gap-2 text-left"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <div>
            <div className="font-medium">{g.email}</div>
            <div className="text-xs text-muted-foreground">
              {g.attive} attive su {g.aziende}
              {g.scaglione &&
                ` · ${fmtEuro(g.scaglione.prezzo_netto_cents)} + IVA/azienda (${g.scaglione.da}${
                  g.scaglione.a ? `–${g.scaglione.a}` : "+"
                })`}
              {` · scadenza ${fmtDate(g.scadenza)}`}
              {` · abbonamento ${sub}`}
            </div>
          </div>
        </button>
        {canImpersonate && (
          <Button size="sm" variant="secondary" onClick={onImpersonate}>
            <UserCog className="mr-1 h-4 w-4" />
            Accedi come gestore
          </Button>
        )}
      </div>
      {open && (
        <div className="border-t bg-muted/30">
          {g.elenco.length === 0 && (
            <p className="px-4 py-2 text-xs text-muted-foreground">Nessuna azienda.</p>
          )}
          {g.elenco.map((a) => (
            <div
              key={a.id}
              className="flex flex-wrap items-center justify-between gap-2 border-b px-8 py-2 text-xs last:border-0"
            >
              <div>
                <div className="font-medium">
                  {a.ragione_sociale}
                  {a.piva && <span className="text-muted-foreground"> · P.IVA {a.piva}</span>}
                </div>
                <div className="text-muted-foreground">
                  {AZIENDA_STATUS[a.status] ?? a.status} · firma: {a.email_firma ?? "il gestore"} ·{" "}
                  <span className="font-mono">{a.account_email}</span>
                </div>
              </div>
              {canImpersonate && (a.status === "active" || a.status === "deactivating") && (
                <Button size="sm" variant="outline" onClick={() => onEnterAzienda(a.id, a.ragione_sociale)}>
                  <Building2 className="mr-1 h-3.5 w-3.5" />
                  Entra come gestore
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
